import { __decorate } from "tslib";
import { customElement, property, state } from 'lit/decorators.js';
import '../icon/icon.js';
import { html } from 'lit';
import { componentStyles } from '../../shared/styles/component.styles.js';
import { formControlStyles } from '../../shared/styles/form-control.styles.js';
import { HlxSelectOption } from './select-option.js';
import { autoUpdate, computePosition, offset, size } from '@floating-ui/dom';
import { watch } from '../../shared/directives/watch.decorator.js';
import { textFieldStyles } from '../text-field/text-field.styles.js';
import { classMap } from 'lit/directives/class-map.js';
import { localized, msg } from '@lit/localize';
import { SlotController } from '../../shared/controllers/slot.controller.js';
import { selectStyles } from './select.styles.js';
import { selectOptionStyles } from './select-option.styles.js';
import { provide } from '@lit/context';
import { selectedValueContext } from './select-context.js';
import { createRef, ref } from 'lit-html/directives/ref.js';
import { PopupSearchStringManager } from '../../shared/popup-search-string-manager.js';
import { HelixFormControl } from '../../shared/forms/helix-form-control.js';
import { createValidity, findForm, sameValidity } from '../../shared/forms/form-utils.js';
let HlxSelect = class HlxSelect extends HelixFormControl {
    constructor() {
        super(...arguments);
        this.label = '';
        this.placeholder = msg('Make selection');
        this.helpText = '';
        this.open = false;
        this.options = [];
        this.hasFocus = false;
        this.inputEl = createRef();
        this.inputContainerEl = createRef();
        this.optionsDialogEl = createRef();
        this.optionsSlotRef = createRef();
        this.searchStringManager = new PopupSearchStringManager();
        this.slotController = new SlotController(this, 'label');
    }
    render() {
        return html `
            <div
                part="hlx-form-control"
                class="${classMap({
            ...this.validation.classMap,
            'hlx-form-control-md': true,
            'hlx-form-control-has-label': !!this.label || this.slotController.hasSlot('label'),
            'hlx-form-control-has-help-text': !!this.helpText,
        })}">
                <label
                    part="hlx-form-control-label"
                    class="hlx-form-control-label"
                    for="text"
                    id="label">
                    <slot name="label">${this.label}</slot>
                </label>

                <div
                    part="base"
                    class=${classMap({
            'hlx-text-field': true,
            'hlx-text-field-md': true,
            'hlx-select-text-field': true,
            'hlx-text-field-disabled': this.disabled,
            'hlx-text-field-focused': this.hasFocus,
        })}
                    @click=${this.togglePopup}
                    ${ref(this.inputContainerEl)}>
                    <div
                        part="input"
                        id="text"
                        role="combobox"
                        aria-labelledby="label"
                        aria-controls="hlx-select-popup-container"
                        aria-haspopup="listbox"
                        aria-expanded=${this.open}
                        aria-readonly="true"
                        aria-autocomplete="none"
                        class="${classMap({
            'hlx-text-field-control': true,
            'hlx-select-text-field-control': true,
        })}"
                        tabindex="0"
                        @focus=${this.handleInputFocus}
                        ${ref(this.inputEl)}>
                        ${this.resolveValueLabel()}
                        <dialog
                            part="popup-container"
                            class="hlx-select-popup-container"
                            id="hlx-select-popup-container"
                            ${ref(this.optionsDialogEl)}
                            @hlx-select-option-selected=${this.handleOptionSelect}>
                            <div role="listbox" aria-labelledby="label">
                                <slot
                                    ${ref(this.optionsSlotRef)}
                                    @focusin=${this.switchFocusedOptionIfNeeded}
                                    @slotchange=${this.synchronizeOptionsWithSlot}
                                    @click=${this.handleOptionClick}></slot>
                            </div>
                        </dialog>
                    </div>

                    <div
                        part="suffix"
                        class="hlx-text-field-suffix"
                        @pointerdown=${(event) => event.preventDefault()}>
                        <div class="hlx-select-icon-container">
                            <hlx-icon
                                decorative
                                name=${this.open ? 'chevron-up' : 'chevron-down'}></hlx-icon>
                        </div>
                    </div>
                </div>
                ${this.validation.renderErrorSlot()}
                <slot
                    name="help-text"
                    part="hlx-form-control-help-text"
                    id="help-text"
                    class="${classMap({
            'hlx-form-control-help-text': true,
            'hlx-form-control-help-text-disabled': this.disabled,
        })}"
                    aria-hidden=${!!this.helpText ? 'false' : 'true'}>
                    ${this.helpText}
                </slot>
            </div>
        `;
    }
    connectedCallback() {
        super.connectedCallback();
        this.handleInputKeyPress = this.handleInputKeyPress.bind(this);
        this.addEventListener('keydown', this.handleInputKeyPress);
        this.handleFocusout = this.handleFocusout.bind(this);
        this.addEventListener('focusout', this.handleFocusout);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.cleanupPopup();
        this.removeEventListener('keydown', this.handleInputKeyPress);
    }
    synchronizeDialogOpenState() {
        if (!this.open) {
            this.optionsDialogEl.value?.close();
        }
        else {
            if (!this.optionToFocus) {
                this.optionToFocus = this.findSelectedOption() ?? this.options[0];
            }
            this.optionsDialogEl.value?.show();
            this.optionToFocus?.focus();
        }
    }
    get validity() {
        if (this.validation.hasCustomValidity) {
            return createValidity('customError');
        }
        if (this.required && !this.value) {
            return createValidity('valueMissing');
        }
        return createValidity();
    }
    getDefaultErrorMessage(validity) {
        if (validity.valueMissing) {
            return msg('Please select an item in the list.');
        }
        return '';
    }
    getForm() {
        return findForm(this);
    }
    focus(options) {
        this.inputEl.value?.focus(options);
    }
    synchronizeSearchStringManagerOptions() {
        this.searchStringManager.options = this.options;
    }
    firstUpdated() {
        this.cleanupPopup = autoUpdate(this.inputContainerEl.value, this.optionsDialogEl.value, () => this.initPopup());
    }
    selectValue(value) {
        if (value === this.value) {
            return;
        }
        this.value = value;
        const validity = this.validity;
        if (validity.valid || !sameValidity(validity, this.lastValidity)) {
            // hide errors immediately
            this.validation.showError = false;
            this.validation.errorText = '';
        }
        this.emit('hlx-change');
    }
    synchronizeOptionsWithSlot() {
        this.options = this.optionsSlotRef.value
            .assignedElements()
            .filter((child) => child instanceof HlxSelectOption);
    }
    handleOptionClick(event) {
        // Stopping propagation so that the 'togglePopup' handler is not called.
        // The popup will be closed anyway in the 'handleOptionSelect' handler.
        event.stopPropagation();
    }
    handleOptionSelect(event) {
        event.stopPropagation();
        this.selectValue(event.detail.value);
        this.open = false;
        this.inputEl.value?.focus();
    }
    handleFocusout(event) {
        if (event.relatedTarget instanceof HlxSelectOption &&
            this.options.includes(event.relatedTarget)) {
            return;
        }
        if (event.target instanceof HlxSelectOption) {
            this.selectValue(event.target.value);
        }
        this.open = false;
        this.hasFocus = false;
        this.emit('hlx-blur');
    }
    switchFocusedOptionIfNeeded() {
        if (this.optionToFocus) {
            this.optionToFocus.focus();
            this.optionToFocus = undefined;
        }
    }
    togglePopup() {
        if (!this.disabled) {
            this.open = !this.open;
            this.inputEl.value?.focus();
        }
    }
    resolveValueLabel() {
        return (this.findSelectedOption()?.getRenderedContent() ??
            html `
                <div class="hlx-select-placeholder">${this.placeholder}</div>
            `);
    }
    handleInputKeyPress(event) {
        if (event.code === 'Escape') {
            this.open = false;
        }
        const focusedOption = this.findFocusedOption();
        if (event.code === 'Space' || event.code === 'Enter') {
            if (focusedOption) {
                this.selectValue(focusedOption.value);
            }
            this.open = !this.open;
            event.preventDefault();
            return;
        }
        if (event.code === 'ArrowDown' && this.options.length) {
            event.preventDefault();
            if (!this.open) {
                this.open = true;
                return;
            }
            this.focusNextOption();
        }
        if (event.code === 'ArrowUp' && this.options.length) {
            event.preventDefault();
            if (!this.open) {
                this.open = true;
                this.optionToFocus = this.options[0];
                return;
            }
            this.focusPrevOption();
        }
        let justOpened = false;
        if (this.searchStringManager.isPrintableChar(event) && !this.open) {
            this.open = true;
            justOpened = true;
        }
        const optionToFocusFromSearchString = this.searchStringManager.getOptionToFocus(event, focusedOption);
        if (justOpened) {
            this.optionToFocus = optionToFocusFromSearchString;
            return;
        }
        if (optionToFocusFromSearchString) {
            optionToFocusFromSearchString.focus();
        }
    }
    focusNextOption() {
        if (!this.options) {
            return;
        }
        const focusedOptionIndex = this.findFocusedOptionIndex();
        let optionToFocus;
        if (focusedOptionIndex < 0) {
            optionToFocus = this.findSelectedOption() ?? this.options[0];
        }
        else if (focusedOptionIndex + 1 < this.options.length) {
            optionToFocus = this.options[focusedOptionIndex + 1];
        }
        optionToFocus?.focus();
    }
    focusPrevOption() {
        if (!this.options) {
            return;
        }
        const focusedOptionIndex = this.findFocusedOptionIndex();
        let optionToFocus;
        if (focusedOptionIndex < 0) {
            optionToFocus = this.findSelectedOption() ?? this.options[0];
        }
        else if (focusedOptionIndex > 0) {
            optionToFocus = this.options[focusedOptionIndex - 1];
        }
        optionToFocus?.focus();
    }
    findFocusedOptionIndex() {
        return this.options.findIndex((option) => option.matches(':focus'));
    }
    findFocusedOption() {
        return this.options.find((option) => option.matches(':focus'));
    }
    findSelectedOption() {
        if (!this.value) {
            return undefined;
        }
        return this.options?.find((option) => option.value === this.value);
    }
    handleInputFocus() {
        this.hasFocus = true;
        this.emit('hlx-focus');
    }
    async initPopup() {
        const { x, y } = await computePosition(this.inputContainerEl.value, this.optionsDialogEl.value, {
            placement: 'bottom',
            middleware: [
                offset(4),
                size({
                    apply({ rects, elements }) {
                        Object.assign(elements.floating.style, {
                            width: `${rects.reference.width}px`,
                        });
                    },
                }),
            ],
        });
        Object.assign(this.optionsDialogEl.value.style, {
            left: `${x}px`,
            top: `${y}px`,
        });
    }
};
HlxSelect.styles = [
    componentStyles,
    formControlStyles,
    textFieldStyles,
    selectStyles,
    selectOptionStyles,
];
__decorate([
    provide({ context: selectedValueContext }),
    property({ type: String })
], HlxSelect.prototype, "value", void 0);
__decorate([
    property({ type: String })
], HlxSelect.prototype, "label", void 0);
__decorate([
    property({ type: String })
], HlxSelect.prototype, "placeholder", void 0);
__decorate([
    property({ type: String, attribute: 'help-text' })
], HlxSelect.prototype, "helpText", void 0);
__decorate([
    state()
], HlxSelect.prototype, "open", void 0);
__decorate([
    state()
], HlxSelect.prototype, "options", void 0);
__decorate([
    state()
], HlxSelect.prototype, "hasFocus", void 0);
__decorate([
    watch('open')
], HlxSelect.prototype, "synchronizeDialogOpenState", null);
__decorate([
    watch('options')
], HlxSelect.prototype, "synchronizeSearchStringManagerOptions", null);
HlxSelect = __decorate([
    customElement('hlx-select'),
    localized()
], HlxSelect);
export { HlxSelect };
//# sourceMappingURL=select.js.map